import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, MessageSquare, Code, Image, Video, Music, Workflow, PenTool, Palette, BarChart3, Rocket } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import toolsData from "@/data/publicTools.json";

const CAT_ICONS = {
  chat: MessageSquare, coding: Code, image: Image, video: Video, audio: Music,
  automation: Workflow, writing: PenTool, design: Palette, data: BarChart3, build: Rocket,
};

export default function PublicTools() {
  useEffect(() => {
    document.title = "Learn 39+ AI Tools — Free Structured Courses | AI Pathshaalaa";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="public-tools-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
        <p className="font-mono text-xs font-bold uppercase tracking-widest text-primary inline-flex items-center gap-1.5">
          <Wrench className="h-4 w-4" /> AI tool courses
        </p>
        <h1 className="mt-2 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl">
          Master any AI tool. <span className="gradient-text">Lesson by lesson.</span>
        </h1>
        <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl">
          A complete, structured course for every popular AI tool — 11 lessons from "what is it" to building a real
          project, curated from the best free YouTube tutorials.
        </p>

        {toolsData.categories.map((cat) => {
          const tools = toolsData.tools.filter((t) => t.category === cat.key);
          if (!tools.length) return null;
          const Icon = CAT_ICONS[cat.key] || Wrench;
          return (
            <section key={cat.key} className="mt-10">
              <h2 className="font-heading font-bold text-lg inline-flex items-center gap-2">
                <span className="grid place-items-center h-8 w-8 rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
                {cat.label}
              </h2>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {tools.map((t) => (
                  <Link key={t.slug} to={`/ai-tools/${t.slug}`} data-testid={`public-tool-card-${t.slug}`}
                    className="group flex flex-col rounded-2xl border border-border bg-card p-5 shadow-sm hover:border-primary/40 hover:shadow-md transition-all">
                    <h3 className="font-heading font-bold group-hover:text-primary transition-colors">{t.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground flex-1">{t.tagline}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                      11-lesson course <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </main>
      <Footer />
    </div>
  );
}
